// Saved per-person home widget boards (Aug 26, 2026).
//
// Thin data layer over `user_widget_layout` — the editable half of the widget
// registry. A row here beats registry.ts's hand-set WIDGET_OVERRIDES_BY_MEMBER,
// which beats DEFAULT_WIDGETS_BY_ROLE. Deleting a row falls back to the code
// defaults, so "Reset to default" is a DELETE, never an empty array.
//
// RLS: everyone reads (a person must read their own row to render home);
// only admin / sales_director / director_of_operations write.

import { supabase } from "./supabase";
import { WIDGETS, type WidgetKey } from "../components/home/widgets/registry";

export interface WidgetLayoutRow {
  member_id: string;
  widgets: WidgetKey[];
  updated_by: string | null;
  updated_at: string;
}

/** Drop anything that isn't a live WidgetKey — a row written before a widget was renamed/retired. */
function sanitize(raw: unknown): WidgetKey[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  return raw.filter((k): k is WidgetKey => {
    if (typeof k !== "string" || seen.has(k) || !(k in WIDGETS)) return false;
    seen.add(k);
    return true;
  });
}

/** One person's saved board. `null` = nothing saved (use the code default). */
export async function fetchWidgetLayout(memberId: string): Promise<WidgetKey[] | null> {
  const { data, error } = await supabase
    .from("user_widget_layout")
    .select("widgets")
    .eq("member_id", memberId)
    .maybeSingle();
  if (error || !data) return null;
  return sanitize((data as { widgets: unknown }).widgets);
}

/** Every saved board, keyed by member id — for the /team-boards roster list. */
export async function fetchAllWidgetLayouts(): Promise<Record<string, WidgetLayoutRow>> {
  const { data, error } = await supabase
    .from("user_widget_layout")
    .select("member_id,widgets,updated_by,updated_at");
  if (error || !data) return {};
  const out: Record<string, WidgetLayoutRow> = {};
  for (const r of data as Array<{ member_id: string; widgets: unknown; updated_by: string | null; updated_at: string }>) {
    out[r.member_id] = {
      member_id: r.member_id,
      widgets: sanitize(r.widgets),
      updated_by: r.updated_by,
      updated_at: r.updated_at,
    };
  }
  return out;
}

/** Save a custom board. Returns an error string, or null on success. */
export async function saveWidgetLayout(
  memberId: string,
  widgets: WidgetKey[],
  actorId: string | null,
): Promise<string | null> {
  const { error } = await supabase
    .from("user_widget_layout")
    .upsert(
      {
        member_id: memberId,
        widgets,
        updated_by: actorId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "member_id" },
    );
  // RLS denials surface as an error here; a silent 0-row write isn't possible
  // on upsert (the WITH CHECK fires), so a null error means it landed.
  return error ? error.message : null;
}

/** Clear the custom board so the person falls back to their code default. */
export async function resetWidgetLayout(memberId: string): Promise<string | null> {
  const { error } = await supabase
    .from("user_widget_layout")
    .delete()
    .eq("member_id", memberId);
  return error ? error.message : null;
}
